import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Calendar,
  Globe,
  Home,
  LayoutDashboard,
  Languages,
  MapPin,
} from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { supabase } from '@/integrations/supabase/client';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SearchClub {
  id: string;
  name: string;
}

interface SearchStadium {
  id: string;
  name: string;
}

interface SearchEvent {
  id: string;
  name: string;
}

const navDestinations = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { label: 'Clubs', href: '/admin/clubs', icon: Building2 },
  { label: 'Stadiums', href: '/admin/stadiums', icon: Home },
  { label: 'Events', href: '/admin/events', icon: Calendar },
  { label: 'Languages', href: '/admin/languages', icon: Globe },
  { label: 'Translations', href: '/admin/translations', icon: Languages },
];

/**
 * Internal-admin ⌘K palette. Loads a lightweight snapshot of clubs, stadiums
 * and recent events once per open, and lets staff jump straight to a nav
 * destination or an entity's admin page. cmdk fuzzy-filters on the typed query.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<SearchClub[]>([]);
  const [stadiums, setStadiums] = useState<SearchStadium[]>([]);
  const [events, setEvents] = useState<SearchEvent[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;

    let cancelled = false;

    Promise.all([
      supabase.from('clubs').select('id, name').is('deleted_at', null),
      supabase.from('stadiums').select('id, name'),
      supabase
        .from('events')
        .select('id, name')
        .order('kickoff_datetime', { ascending: false })
        .limit(50),
    ]).then(([clubsRes, stadiumsRes, eventsRes]) => {
      if (cancelled) return;
      setClubs(clubsRes.data ?? []);
      setStadiums(stadiumsRes.data ?? []);
      setEvents(eventsRes.data ?? []);
      setLoaded(true);
    });

    return () => {
      cancelled = true;
    };
  }, [open, loaded]);

  const go = (href: string) => {
    navigate(href);
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search clubs, venues, fixtures…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Go to">
          {navDestinations.map((item) => (
            <CommandItem
              key={item.href}
              value={`nav-${item.href}`}
              keywords={[item.label]}
              onSelect={() => go(item.href)}
            >
              <item.icon className="mr-2 h-4 w-4" strokeWidth={1.8} />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        {clubs.length > 0 && (
          <CommandGroup heading="Clubs">
            {clubs.map((club) => (
              <CommandItem
                key={club.id}
                value={`club-${club.id}`}
                keywords={[club.name]}
                onSelect={() => go('/admin/clubs')}
              >
                <Building2 className="mr-2 h-4 w-4" strokeWidth={1.8} />
                <span>{club.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {stadiums.length > 0 && (
          <CommandGroup heading="Stadiums">
            {stadiums.map((stadium) => (
              <CommandItem
                key={stadium.id}
                value={`stadium-${stadium.id}`}
                keywords={[stadium.name]}
                onSelect={() => go(`/admin/stadiums/${stadium.id}/seats`)}
              >
                <MapPin className="mr-2 h-4 w-4" strokeWidth={1.8} />
                <span>{stadium.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {events.length > 0 && (
          <CommandGroup heading="Events">
            {events.map((event) => (
              <CommandItem
                key={event.id}
                value={`event-${event.id}`}
                keywords={[event.name]}
                onSelect={() => go('/admin/events')}
              >
                <Calendar className="mr-2 h-4 w-4" strokeWidth={1.8} />
                <span>{event.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
